import { firmDisplayName } from '../../intake/IntakeBrandScope';
import { useState, type ReactNode } from 'react';
import { hasTermsAcceptance } from '@steward-app/steward-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { listFactors, FACTORS_QUERY_KEY } from '../../api/mfa';
import { isUsableFactor } from '../../api/types';
import { ME_QUERY_KEY, meQueryOptions } from '../../lib/meQuery';
import { recordTermsAcceptance } from '../../terms/recordTermsAcceptance';
import { onboardingGreetingName } from '../../workspace/userName';
import { useAccessToken, useAuth } from '../../auth/useAuth';
import { forgetSignedOutCard, hostFirm, rememberSignedOutCard } from '../../auth/signedOutCard';
import { TesterFeedback } from '../../testerfeedback/TesterFeedback';
import { TesterFeedbackScreen } from '../../testerfeedback/screen';
import { FullPageLoading } from '../../components/FullPageLoading';
import type { MeResponse } from '../../api/types';
import { currentOnboardingInvitation, isOnboardingClient } from './isOnboardingClient';
import { getOnboardingForm, onboardingFormQueryKey } from '../../api/onboardingForm';
import { OnboardingBackToAppContext, OnboardingEnrollment, OnboardingLoadError, OnboardingWelcome } from './OnboardingClientScreens';
import { onboardingRecordQuery, isTransientReadFailure } from './onboardingRecordQuery';
import { OnboardingForm, sentAtOf } from './OnboardingForm';

export const ONBOARDING_PATH = '/onboarding';
export const ONBOARDING_FORM_PATH = '/onboarding/form';
/** The completed page (#1976). The form stays mounted under it, see `OnboardingFormBody`. */
export const ONBOARDING_DONE_PATH = '/onboarding/done';
/** The client's requests page (#2433): where the request email and its reminder link. The same
 *  mounted form component as the other two, so it adds no read, no write and no route to the API. */
export const ONBOARDING_REQUESTS_PATH = '/onboarding/requests';

/**
 * `/onboarding`: the welcome page, unless this client has already sent the form (#1976), when it
 * is the completed page instead. Only the form's own read knows that (the record's `onboardingSentAt`, or the legacy `details.attributes.sentAt`,
 * neither of which `/me` carries), so the read happens here, under the same query key the form uses.
 * It refetches on every mount (`staleTime: 0`), since the form's own copy is `staleTime: Infinity`
 * and not updated by saves, and waits for that fresh read rather than flashing the welcome card
 * from an older one.
 *
 * Anything but a clean "sent and open" read shows the welcome page, as before this read existed,
 * and so does any read while no Terms acceptance is on record (steward-app#2630), so that Start
 * can record it:
 * a failed or still-preparing read (the form handles its own errors once Start opens it) and a
 * closed form (the closed notice wins over the completed page; Start reaches it).
 * The cost: this read needs the step-up the form's reads need, so it can now be asked for on the
 * welcome page rather than at Start.
 */
function OnboardingLanding({ token, firmWorkspaceId, firmName, givenName, termsAccepted, onStart, onSignOut }: {
  token: string;
  firmWorkspaceId: string;
  firmName: string | null;
  givenName?: string;
  /** No Terms acceptance on record: the welcome page, never the completed one, so its Start can record it. */
  termsAccepted: boolean;
  onStart: () => Promise<void>;
  onSignOut?: () => void;
}) {
  const q = useQuery({
    queryKey: onboardingFormQueryKey(firmWorkspaceId),
    queryFn: () => getOnboardingForm(token, firmWorkspaceId),
    staleTime: 0,
    retry: (failures, err) => failures < 1 && isTransientReadFailure(err),
  });
  // The record's stamp is where a send lands now (docs/43 slice 4c); the legacy stamp still counts
  // for a form sent before the move. A record that cannot be read (not ready, failed) is "not sent".
  const slug = q.data?.firmSlug;
  const rq = useQuery(onboardingRecordQuery(token, slug, !!q.data && !q.data.formClosed));
  if (!q.isFetchedAfterMount && !q.isError) return <FullPageLoading />;
  if (slug && q.data && !q.data.formClosed && rq.isFetching && !rq.data && !rq.isError) return <FullPageLoading />;
  const sent = !!rq.data?.onboardingSentAt || (q.data ? !!sentAtOf(q.data) : false);
  if (termsAccepted && q.data && !q.data.formClosed && sent) return <Navigate to={ONBOARDING_DONE_PATH} replace />;
  return <OnboardingWelcome firmName={firmName} givenName={givenName} onStart={onStart} onSignOut={onSignOut} />;
}

/**
 * Where a client invited straight to onboarding lands (crossmarc/steward-app#1764).
 *
 * Sits in `router.tsx` between `FirstRunGate` and `EnrollmentGate`. For anyone who is not an
 * onboarding client ({@link isOnboardingClient}) it renders its children and changes nothing.
 * For an onboarding client it renders INSTEAD of the app:
 *
 *   1. no usable sign-in factor → `OnboardingEnrollment` (passkey, or an authenticator app);
 *   2. `/onboarding/form` → the onboarding form (#1765), for the most recent invitation's firm;
 *   3. `/onboarding/done` → the same form component, on its completed page (#1976);
 *   4. `/onboarding/requests` → the same form component, on the requests page (#2433), whether or not
 *      the form has been sent: the request email links here;
 *   5. `/onboarding` → the welcome page, or a redirect to the completed page for a client who has sent;
 *   6. any other path → a redirect to `/onboarding`.
 *
 * So such a client never reaches `WorkspaceProvider`, and so never sees "create your workspace",
 * the workspace picker, or any page that assumes a workspace. That is the point: they signed up
 * to fill in one firm's form, and nothing they did gave them a workspace or the right to make
 * one (`canBootstrapTenant === false` is part of the test).
 *
 * Unlike `FirstRunGate` and `EnrollmentGate`, this does NOT depend on `mfaEnrollmentEnabled()`:
 * it routes a client who is about to hand over a Social Security number to enroll a second
 * factor first, in every environment. The reads share their keys with those gates (`['me']`,
 * `FACTORS_QUERY_KEY`), so React Query serves one request to all three.
 *
 * Like `EnrollmentGate`, this gate is UX and MUST NOT be treated as the boundary. It makes the
 * order graceful; it enforces nothing, since a client can call the API without this SPA. The
 * control that counts sits server-side on the form's own reads and writes (#1765): appcore's
 * `/me/onboarding-forms` gate (the caller's own grant from that firm) and a step-up on every
 * one of them.
 *
 * A person who ALREADY has a workspace (or a pending workspace invite) and is then invited to
 * onboarding is not an onboarding client, so this gate passes them through. They reach the same
 * screens at the same four paths through {@link OnboardingInShell}, which `router.tsx` mounts on
 * a sibling `/onboarding` route under the same gates but OUTSIDE `Layout` (#2465).
 *
 * A failed `/me` read passes through to the children, as `FirstRunGate` does, since this gate
 * cannot tell who the caller is without it. A failed factors read for a KNOWN onboarding client
 * shows a retry rather than passing through, because passing through would land them in the
 * workspace flow this gate exists to keep them out of.
 */
export function OnboardingClientGate({ children }: { children: ReactNode }) {
  const token = useAccessToken();
  const { signOut } = useAuth();

  const meQuery = useQuery(meQueryOptions(token));
  const onboarding = isOnboardingClient(meQuery.data);

  if (!token) return null;
  if (meQuery.isLoading) return <FullPageLoading />;
  if (!onboarding) return <>{children}</>;

  // The signed-out card's note (#2432) goes first, then the unchanged full sign-out. It returns
  // to `/login`, which shows the card for this host's firm (`auth/signedOutCard.ts`). `outcome`
  // may be a click event on a screen that hands this straight to an onClick: only a literal
  // `true` counts as sent, and the screens that know the form was sent say so.
  const onSignOut = (outcome?: { sent?: boolean; requests?: boolean }) => {
    rememberSignedOutCard({ form: 'onboarding', firm: hostFirm()?.firmId ?? null, sent: outcome?.sent === true, requests: outcome?.requests === true });
    void (async () => { try { await signOut(); } catch { forgetSignedOutCard(); } })();
  };
  return <OnboardingScreens token={token} me={meQuery.data} onSignOut={onSignOut} />;
}

/**
 * The onboarding screens themselves, shared by the takeover ({@link OnboardingClientGate}) and the
 * signed-in version ({@link OnboardingInShell}, #2465): the second-factor check, then the welcome
 * page, form, completed page or requests page for the most recent invitation, chosen by the URL.
 *
 * The two differ only in the corner control, one slot (`LeaveControl`): the takeover passes
 * `onSignOut` and every screen shows "Sign out"; the signed-in version passes `backToApp` and the
 * same slot shows a "Back to Steward" link to `/` instead.
 */
export function OnboardingScreens({ backToApp = false, ...rest }: {
  token: string;
  me: MeResponse | undefined;
  onSignOut?: (outcome?: { sent?: boolean; requests?: boolean }) => void;
  backToApp?: boolean;
}) {
  // The tester feedback tab (crossmarc/steward-app#2531, TEMPORARY): by the HOST's firm, so it
  // shows on a listed firm's own branded host and nowhere else.
  return (
    <TesterFeedback firmId={hostFirm()?.firmId} surface="onboarding" email={rest.me?.email}>
      <OnboardingBackToAppContext.Provider value={backToApp}><OnboardingScreensBody {...rest} /></OnboardingBackToAppContext.Provider>
    </TesterFeedback>
  );
}

function OnboardingScreensBody({ token, me, onSignOut }: {
  token: string;
  me: MeResponse | undefined;
  onSignOut?: (outcome?: { sent?: boolean; requests?: boolean }) => void;
}) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const invitation = currentOnboardingInvitation(me);
  // The firm as every page of this journey names it: the brand's short name where the firm has a
  // brand, else its own name (steward-app#2770). Server mail and stamps keep the legal name.
  const firmName = firmDisplayName(invitation?.brandKey, invitation?.firmName);
  // steward-app#2630: the façade refuses every form write without it, and a saved answer is
  // readable by the firm before any send. So the form opens only once it is on record, and the
  // welcome page's Start (which carries the line) is where it is recorded.
  // `recordedHere` too: the refetched `/me` reaches this component a tick after the navigate, and
  // without it the form path would bounce straight back to the welcome page.
  const [recordedHere, setRecordedHere] = useState(false);
  const termsAccepted = recordedHere || hasTermsAcceptance(me?.preferences as Record<string, unknown> | undefined);
  async function start() {
    if (!termsAccepted) {
      await recordTermsAcceptance(token);
      setRecordedHere(true);
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    }
    // Back to the page the client came for (a request email links to `/onboarding/requests`),
    // when the redirect below sent them here for the Terms.
    const after = (location.state as { afterTerms?: unknown } | null)?.afterTerms;
    navigate(after === ONBOARDING_DONE_PATH || after === ONBOARDING_REQUESTS_PATH ? after : ONBOARDING_FORM_PATH);
  }

  const factorsQuery = useQuery({
    queryKey: FACTORS_QUERY_KEY,
    queryFn: () => listFactors(token),
    staleTime: 60_000,
    retry: 2,
  });

  if (factorsQuery.isLoading) return <FullPageLoading />;
  if (factorsQuery.isError) {
    return (
      <>
        <TesterFeedbackScreen name="Onboarding: could not load" />
        <OnboardingLoadError onRetry={() => void factorsQuery.refetch()} onSignOut={onSignOut} />
      </>
    );
  }

  const hasFactor = (factorsQuery.data ?? []).some(isUsableFactor);
  if (!hasFactor) {
    return (
      <>
        <TesterFeedbackScreen name="Onboarding: set up a second check" />
        <OnboardingEnrollment
          token={token}
          firmName={firmName}
          onEnrolled={() => void queryClient.invalidateQueries({ queryKey: FACTORS_QUERY_KEY })}
          onSignOut={onSignOut}
        />
      </>
    );
  }

  const path = location.pathname.replace(/\/+$/, '');
  if ((path === ONBOARDING_FORM_PATH || path === ONBOARDING_DONE_PATH || path === ONBOARDING_REQUESTS_PATH) && invitation) {
    if (!termsAccepted) return <Navigate to={ONBOARDING_PATH} replace state={{ afterTerms: path }} />;
    // One element for all three paths, so React keeps the instance (and the client's edits) across
    // the send, "Review my answers" and the requests page; the URL follows the view, and the view
    // follows the URL.
    return (
      <OnboardingForm
        token={token}
        firmWorkspaceId={invitation.firmWorkspaceId}
        firmName={firmName}
        onSignOut={onSignOut}
        view={path === ONBOARDING_DONE_PATH ? 'completed' : path === ONBOARDING_REQUESTS_PATH ? 'requests' : 'form'}
        onViewChange={(v) => navigate(v === 'completed' ? ONBOARDING_DONE_PATH : v === 'requests' ? ONBOARDING_REQUESTS_PATH : ONBOARDING_FORM_PATH)}
      />
    );
  }
  if (path === ONBOARDING_PATH && invitation) {
    return (
      <>
        <TesterFeedbackScreen name="Onboarding: welcome" />
        <OnboardingLanding
          token={token}
          firmWorkspaceId={invitation.firmWorkspaceId}
          firmName={firmName}
          givenName={onboardingGreetingName(me)}
          termsAccepted={termsAccepted}
          onStart={start}
          onSignOut={onSignOut}
        />
      </>
    );
  }
  return <Navigate to={ONBOARDING_PATH} replace />;
}

/**
 * The four `/onboarding*` routes for a person who is NOT an onboarding client (#2465): signed in
 * with a workspace, then invited by a firm to its onboarding form or sent requests. The request
 * and onboarding emails link to these paths, and until this existed every one of them redirected
 * home, so the person could not answer.
 *
 * Mounted by `router.tsx` OUTSIDE `Layout` (the screens draw their own page, as for the takeover),
 * under the same gates, so it runs only for someone the gate passed through. It renders {@link OnboardingScreens} for the most recent invitation
 * ({@link currentOnboardingInvitation}), with "Back to Steward" where the takeover has Sign out.
 * With no invitation on `/me` there is nothing to show, and the path redirects home as before.
 * So does an invitation whose `formClosed` is `true`: the firm has set up Steward for this person
 * (`onboardingFormClosedFor`), their client record may be gone since activation, and the form
 * would only fail at Start. An absent `formClosed` (an older façade) shows the screens, as before.
 * The no-workspace takeover does not read `formClosed`.
 *
 * Not a boundary, like the gate: this is a routing label, and the form's reads and writes carry
 * their own checks server-side.
 */
export function OnboardingInShell() {
  const token = useAccessToken();
  const meQuery = useQuery(meQueryOptions(token));
  if (!token) return null;
  if (meQuery.isLoading) return <FullPageLoading />;
  const invitation = currentOnboardingInvitation(meQuery.data);
  if (!invitation || invitation.formClosed === true) return <Navigate to="/" replace />;
  return <OnboardingScreens token={token} me={meQuery.data} backToApp />;
}
